import { CreditCard, CheckCircle2, Percent, CalendarClock } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero, CTABand } from "@/components/Layout";
import { business } from "@/data/site";

const Financing = () => {
  useSeo({
    title: `Financing Options — Affordable Home Repairs | ${business.name} New York`,
    description: "Flexible financing for New York plumbing, electrical & HVAC projects. Don't delay important repairs — spread the cost with convenient payment options.",
  });

  const perks = [
    { icon: CalendarClock, t: "Flexible Terms", d: "Choose a payment plan that fits your monthly budget on qualifying projects." },
    { icon: Percent, t: "Competitive Rates", d: "Affordable financing so a major repair or upgrade doesn't have to wait." },
    { icon: CreditCard, t: "Quick Approval", d: "A fast, simple application process with minimal hassle." },
  ];

  return (
    <>
      <PageHero eyebrow="Payment Options" title="Financing That Fits Your Budget" subtitle="A new HVAC system or major repair shouldn't break the bank. Ask us about flexible financing on qualifying work." />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="financing-perks">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {perks.map((p) => (
            <div key={p.t} className="border border-[#E2E8F0] p-8 hover:shadow-[6px_6px_0px_#0F2E4C] transition-shadow">
              <div className="w-14 h-14 bg-[#059669] flex items-center justify-center mb-5"><p.icon className="w-7 h-7 text-white" /></div>
              <h3 className="font-display font-bold text-2xl mb-3">{p.t}</h3>
              <p className="text-[#475569] leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#0F2E4C] text-white p-8 sm:p-12">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">How Financing Works</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Request your free estimate for the work you need",
              "Ask our team about available financing options",
              "Complete a quick, simple application",
              "Get approved and schedule your service",
            ].map((s, i) => (
              <div key={s} className="flex items-start gap-3"><div className="w-8 h-8 bg-[#059669] flex items-center justify-center shrink-0 font-display font-black">{i + 1}</div> <span className="text-slate-200">{s}</span></div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-8 italic">Financing is subject to credit approval through third-party lending partners. Terms, rates, and availability are placeholders — replace with the client's real financing program details before launch.</p>
        </div>

        <div className="mt-10 flex items-start gap-3 bg-white border border-[#E2E8F0] p-6">
          <CheckCircle2 className="w-6 h-6 text-[#16A34A] shrink-0" />
          <p className="text-[#475569]">Ready to get started? <a href={business.phoneHref} className="text-[#059669] font-bold hover:underline">Call {business.phoneDisplay}</a> and ask about financing today.</p>
        </div>
      </section>

      <CTABand heading="Don't Delay Important Repairs" sub="Ask about flexible financing when you request your free estimate." />
    </>
  );
};

export default Financing;
